import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { UserPlus, Contact } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth, login } from "@/hooks/useAuth";
import { ContactLinks } from "@/components/contacts/ContactLinks";
import { Avatar } from "@/components/friends/Avatar";

// Opened by someone who scanned a swap QR with their phone camera
export default function SwapPage() {
  const { token } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const { data: p, error } = useQuery({ queryKey: ["swap", token], queryFn: () => api.swapInfo(token), retry: false });
  const mine = user && p && user.user_id === p.user_id;

  const connect = async () => {
    if (!user) {
      localStorage.setItem("bl_swap", token);
      return login();
    }
    setBusy(true);
    try {
      const r = await api.swapAccept(token);
      toast.success(`You and ${r.name.split(" ")[0]} are now friends`);
      navigate(`/friend/${r.friend_id}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't add them"); }
    setBusy(false);
  };
  useEffect(() => { document.title = p ? `${p.name} · the bucketlist` : "the bucketlist"; }, [p]);

  if (error) return <Shell><p className="text-slate-300" data-testid="swap-expired">{error?.response?.data?.detail || "This code isn't valid any more."}</p></Shell>;
  if (!p) return <Shell><div className="h-40 animate-pulse rounded-2xl bg-white/5" /></Shell>;
  const vcf = `${process.env.REACT_APP_BACKEND_URL}/api/swap/${token}/contact.vcf`;
  return (
    <Shell>
      <div className="flex items-center gap-4" data-testid="swap-person">
        <Avatar f={p} size="w-16 h-16 text-2xl" />
        <div>
          <p className="eyebrow">You just met</p>
          <h1 className="font-display text-3xl font-bold">{p.name}</h1>
        </div>
      </div>
      {Object.keys(p.card).length > 0 && <div className="mt-5"><ContactLinks c={{ name: p.name, ...p.card }} testId="swap-person-links" /></div>}
      <div className="mt-8 grid gap-3">
        <a href={vcf} className="flex items-center justify-center gap-2 rounded-full bg-white/10 px-5 py-3 font-medium hover:bg-white/15 transition-colors" data-testid="swap-save-contact"><Contact size={18} /> Save to my phone contacts</a>
        {!mine && <button onClick={connect} disabled={busy} className="flex items-center justify-center gap-2 rounded-full bg-emerald-400 px-5 py-3 font-semibold text-[#06121c] hover:bg-emerald-300 transition-colors disabled:opacity-60" data-testid="swap-connect">
          <UserPlus size={18} /> {user ? `Become friends with ${p.name.split(" ")[0]}` : "Make my bucket list & become friends"}
        </button>}
        {mine && <p className="text-sm text-slate-400" data-testid="swap-own-code">This is your own code — show it to someone else.</p>}
      </div>
    </Shell>
  );
}

const Shell = ({ children }) => (
  <main className="min-h-screen bg-[#0B0F17] text-slate-100 px-6 py-16 flex justify-center" data-testid="swap-page">
    <div className="w-full max-w-md">{children}</div>
  </main>
);
